import { makeAutoObservable, runInAction } from 'mobx';
import services from '../services';
import {
    arraysEqual,
    capitalizeFirst,
    extractErrorMessage,
    fixHtml,
} from '../utils/helpers';
import {
    generateCreateEntity,
    generateDeleteEntity,
    generateLoadList,
    generateLoadEntity,
    generateUpdateEntity,
} from '../utils/mobx';
import keyBy from 'lodash/keyBy';
import debounce from 'lodash/debounce';
import debounceFn from 'debounce-fn';

const upper = (lower) => lower.replace(/^\w/, (c) => c.toUpperCase());

class CompanyStore {
    companies = [];
    savingEmailSettings = [];
    savingBrandingSettings = [];
    savingEnrolmentSetting = [];
    savingInviteOnListAddSetting = [];
    savingFastForwardSetting = [];
    savingHideObsoleteBouncesSetting = [];
    savingOrganizeEmployeeListsByOrganization = [];
    savingAwareExposeSettings = [];
    savingRecommendationSettings = [];
    rotatingAwareExposeKey = [];
    awareExposeConfig = null;
    loadingAwareExposeConfig = false;
    onboardingSettings = null;
    loadingOnboardingSettings = false;
    savingOnboardingSettings = false;
    updatingLanguageSettings = false;
    addingManager = false;
    loadingCompanies = false;
    creatingCompany = false;
    loadingIntegrations = false;
    savingLanguage = false;
    savingCompany = false;
    syncRunning = false;
    uploadingLogo = false;
    switchingTrackingAnonymous = false;
    company = null;
    error = null;
    integrationError = null;
    googleError = null;
    azureError = null;
    azuressoError = null;
    slackError = null;
    addManagerError = null;
    previewEmailError = null;
    loadingDefaultEmailSettings = false;
    defaultEmailSettings = null;
    smtpSettings = null;
    loadingSmtpSettings = false;
    savingSmtpSettings = false;

    // SCIM key management
    generatingScimKey = [];
    scimKeyGenerated = null;

    loadingApiKeys = null;
    createApiKey = false;
    apiKeys = [];
    /** API key to show once in modal (create response with raw value) */
    keyToShowOnce = null;
    deletingApiKeys = [];
    deletingApiKey = null;
    deletingIntegration = null;
    updatingApiKeys = [];

    webhooks = [];
    loadingWebhooks = null;
    creatingWebhook = false;
    deletingWebhooks = [];
    updatingWebhooks = [];

    audits = [];
    auditCount = 0;
    loadingAudits = false;
    auditQueryContext = {
        p: 0,
        n: 10,
        operation: [],
        author: '',
        subject: [],
        fromDate: null,
        toDate: null,
        order: null,
    };
    auditFilterOptions = {
        operations: [],
        subjects: [],
    };

    constructor(mainStore, subscriptionStore, commonStore) {
        [
            'azure',
            'google',
            'slack',
            'azuresso',
            'teams',
            'vanta',
            'vantasync',
        ].forEach((name) => {
            this[`${name}Datas`] = [];
            this[`loading${upper(name)}Datas`] = [];
            this[`loading${upper(name)}Groups`] = [];
            this[`saving${upper(name)}Data`] = [];
            this[`testing${upper(name)}Data`] = [];
            this[`${name}Groups`] = [];
        });
        makeAutoObservable(this);
        this.mainStore = mainStore;
        this.subscriptionStore = subscriptionStore;
        this.commonStore = commonStore;

        this.loadIntegrations = generateLoadList(
            'integrations',
            this,
            'loadingIntegrations',
            async (companyId, options) => {
                return services.Companies.integrationsService(companyId).list(
                    options
                );
            },
            'integrations'
        );
    }

    loadApiKeys = generateLoadList(
        'apikeys',
        this,
        'loadingApiKeys',
        async (companyId) => {
            return services.Companies.apiKeysService(companyId).list();
        },
        'apiKeys'
    );

    createNewApiKey = generateCreateEntity(
        'createApiKey',
        this,
        'creatingApiKey',
        async (companyId) => {
            const newKey = await services.Companies.apiKeysService(
                companyId
            ).create({});
            this.apiKeys.push({
                id: newKey.id,
                name: newKey.name,
                maskedValue: newKey.maskedValue,
                active: newKey.active,
                scope: newKey.scope,
                accountId: newKey.accountId,
                companyId: newKey.companyId,
            });
            this.keyToShowOnce = newKey;
            return this.apiKeys;
        },
        'apiKeys'
    );

    clearKeyToShowOnce() {
        this.keyToShowOnce = null;
    }

    deleteApiKey = generateDeleteEntity(
        'deleteApiKey',
        this,
        'deletingApiKey',
        async (key, __, options) => {
            await services.Companies.apiKeysService(options.companyId).delete({
                id: key,
            });
            this.apiKeys = this.apiKeys.filter((x) => x.id !== key);
        }
    );

    setApiKeyName(companyId, id, value) {
        let apiKey = this.apiKeys.find((x) => x.id === id);
        if (apiKey) {
            apiKey.name = value;
            this.updateApiKeyName(companyId, id, value);
        }
    }

    updateApiKeyName = debounce(
        generateUpdateEntity(
            'apikeys',
            this,

            'updatingApiKeys',
            async (companyId, id, name) => {
                return services.Companies.apiKeysService(companyId).update(
                    { name },
                    id
                );
            }
        ),
        300
    );

    loadWebhooks = generateLoadList(
        'webhook',
        this,
        'loadingWebhooks',
        async (companyId) => {
            return services.Companies.WebhooksService(companyId).list();
        },
        'webhooks'
    );

    createWebhook = generateCreateEntity(
        'createWebhook',
        this,
        'creatingWebhook',
        async (companyId, values) => {
            const newWebhook = await services.Companies.WebhooksService(
                companyId
            ).create(values);
            this.webhooks.push(newWebhook);
            return this.webhooks;
        },
        'webhooks'
    );

    updateWebhook = generateUpdateEntity(
        'createWebhook',
        this,
        'updatingWebhooks',
        async (companyId, id, values) => {
            const result = await services.Companies.WebhooksService(
                companyId
            ).update(values, id);

            let webhook = this.webhooks.find((x) => x.id === id);
            if (webhook) {
                Object.assign(webhook, result, {
                    definition: {
                        eventName: values.type,
                        signatureSecret: values.signatureSecret,
                    },
                });
            }
        }
    );

    deleteWebhook = generateDeleteEntity(
        'deleteWebhook',
        this,
        'deletingWebhooks',
        async (id, options) => {
            await services.Companies.WebhooksService(options.companyId).delete({
                id,
            });
            this.webhooks = this.webhooks.filter((x) => x.id !== id);
        }
    );

    get operationOptions() {
        return this.auditFilterOptions.operations
            .slice()
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map((x) => ({
                value: x,
                name: capitalizeFirst(x),
            }));
    }

    get subjectOptions() {
        return this.auditFilterOptions.subjects
            .slice()
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
            .map((x) => ({
                value: x,
                name: capitalizeFirst(x),
            }));
    }

    _normaliseAuditParams() {
        const p = { ...this.auditQueryContext };
        ['operation', 'subject'].forEach((k) => {
            if (Array.isArray(p[k]) && p[k].length) p[k] = p[k].join(',');
            else delete p[k];
        });

        if (p.author != null && typeof p.author === 'string') {
            p.author = p.author.trim();
        } else {
            delete p.author;
        }

        ['fromDate', 'toDate'].forEach((k) => {
            if (p[k] instanceof Date) p[k] = p[k].toISOString();
            else delete p[k];
        });
        return p;
    }

    applyAuditFilter(key, value, companyId) {
        this.auditQueryContext = { ...this.auditQueryContext, [key]: value, p: 0 };
        this._debouncedLoadAudits({companyId});
    }

    clearAuditFilters(companyId) {
        const defaults = {
            p: 0,
            n: 10,
            operation: [],
            author: '',
            subject: [],
            fromDate: null,
            toDate: null,
            order: null,
        };

        const unchanged = Object.keys(defaults).every(
            (k) => JSON.stringify(this.auditQueryContext[k]) === JSON.stringify(defaults[k]),
        );
        if (unchanged) return;

        runInAction(() => {
            this.auditQueryContext = defaults;
        });

        this._debouncedLoadAudits({companyId});
    }


    setAuditPagination(p, n, companyId) {
        const hasChanged = this.auditQueryContext.p !== p || this.auditQueryContext.n !== n;
        if (!hasChanged) return;

        runInAction(() => {
            this.auditQueryContext.p = p;
            this.auditQueryContext.n = n;
        });

        this._loadAuditsRaw({companyId});
    }

    setAuditSortBy(sortBy, companyId) {
        if (!sortBy || !sortBy.length) {
            delete this.auditQueryContext.order;
        } else {
            const { id, desc } = sortBy[0];
            runInAction(() => {
                this.auditQueryContext = {
                    ...this.auditQueryContext,
                    p: 0,
                    order: `${id} ${desc ? 'desc' : 'asc'}`,
                };
            });
        }
        this._debouncedLoadAudits({ companyId });
    }

    get auditPages() {
        if (!this.auditCount || !this.auditQueryContext.n) return 0;

        return Math.ceil(this.auditCount / this.auditQueryContext.n);
    }

    _loadAuditsRaw = generateLoadList(
        'audits',
        this,
        'loadingAudits',
        async ({companyId, signal}) => {
            const r = await services.Companies
                .auditService(companyId)
                .list(this._normaliseAuditParams(), { signal });

            runInAction(() => {
                this.auditCount = r.count
                if(!arraysEqual(this.auditFilterOptions.operations, r.distinctOperations)) {
                    this.auditFilterOptions.operations = r.distinctOperations || [];
                }
                if(!arraysEqual(this.auditFilterOptions.subjects, r.distinctSubjects)) {
                    this.auditFilterOptions.subjects = r.distinctSubjects || [];
                }
            });

            return r.rows;
        },
        'audits',
        undefined,
        {
            abortPrevious: true,
        }
    );


    _debouncedLoadAudits = debounceFn(
        (...args) => this._loadAuditsRaw(...args),
        { wait: 500, trailing: true }
    );

    setError(error, name) {
        switch (name) {
            case 'integrations':
                this.integrationError = error;
                break;
            case 'defaultEmailSettings':
                this.error = error;
                break;
            default:
                this.error = error;
        }
    }

    async loadCompanies(background = false) {
        if (!background) {
            this.loadingCompanies = true;
            this.error = null;
            this.companies.clear();
        }

        try {
            const result = await services.Companies.myCompanies();
            this.companies.replace(result);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingCompanies = false;
        }
    }

    async switchTrackingAnonymous(companyId) {
        this.switchingTrackingAnonymous = true;
        this.error = null;
        try {
            await services.Companies.switchTrackingAnonymous(companyId);
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) company.anonymousTracking = true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.switchingTrackingAnonymous = false;
        }
    }

    async changeLanguage(companyId, value) {
        this.savingLanguage = true;
        this.error = null;
        try {
            await services.Companies.update(
                { defaultLanguage: value },
                companyId
            );
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) company.defaultLanguage = value;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingLanguage = false;
        }
    }

    async openManageSession(companyId) {
        return services.Companies.openManageSession(companyId);
    }

    async handleCreateCompany(partnerId, onFinish) {
        this.creatingCompany = true;
        let result = await this.create(
            {
                partnerId,
                companyName:
                    this.subscriptionStore.companyForm.values().companyName,
                subscriptionCode:
                    this.subscriptionStore.companyForm.values().subscription,
                seats: this.subscriptionStore.companyForm.values().seats,
                validTo: this.subscriptionStore.companyForm.values().valid,
                validTill:
                    this.subscriptionStore.companyForm.values().validTill,
            },
            true
        );

        if (result) {
            this.creatingCompany = false;
            if (onFinish) onFinish();
            this.commonStore.success('Company created.');
        } else {
            this.creatingCompany = false;
            if (this.error) {
                this.commonStore.error(this.error);
            }
        }
    }

    async create(values, loadCompany) {
        this.savingCompany = true;
        try {
            const newCompanyId = values.partnerId
                ? await services.Partners.companiesService(
                      values.partnerId
                  ).create(values)
                : await services.Companies.create(values);
            if (newCompanyId && loadCompany) {
                this.loadCompanies(true);
                const result = await services.Companies.myCompanies();
                this.companies.replace(result);
                let company = this.companies.find(
                    (x) => x.company_id === newCompanyId
                );
                if (company) {
                    this.setCompany(company);
                }
            }

            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingCompany = false;
        }
    }

    async save(companyId, values) {
        this.savingCompany = true;
        try {
            console.log('Saving company', companyId, values);
            let result = await services.Companies.update({
                id: companyId,
                ...values,
            });
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company)
                Object.assign(company, result, { company_name: result.name });
            return true;
        } catch (e) {
            //console.error('Save failed: ', e);
            this.error = extractErrorMessage(e);
        } finally {
            this.savingCompany = false;
        }
    }

    saveLanguageSettings = debounce(
        generateUpdateEntity(
            'languageSettings',
            this,
            'updatingLanguageSettings',
            async (companyId, values, callback) => {
                let body = { 'languages.disabled': [] };
                values
                    .filter((lang) => !lang.enabled)
                    .forEach((x) => {
                        body[`languages.disabled`].push(x.code);
                    });
                let result = await services.Companies.update({
                    id: companyId,
                    ...body,
                });
                let company = this.companies.find(
                    (x) => x.company_id === companyId
                );

                if (company) {
                    Object.assign(company, result);
                }
                if (callback) callback();
            }
        ),
        600
    );

    async saveEmailSettings(companyId, values) {
        if (this.savingEmailSettings.includes(companyId)) return false;

        const dirtyValues = this.getDirtyEmailSettings(values);
        if (Object.keys(dirtyValues).length === 0) {
            return true;
        }

        this.savingEmailSettings.push(companyId);

        try {
            const body = {};
            for (const [key, value] of Object.entries(dirtyValues)) {
                body[`ui.email.${key}`] = value ?? '';
            }
            const result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings;

                for (const [key, val] of Object.entries(dirtyValues)) {
                    settingsObj[`ui.email.${key}`] = val ?? '';
                }
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            this.savingEmailSettings.remove(companyId);
        }
    }

    getDirtyEmailSettings(values = {}) {
        const result = {};
        const currentSettings =
            (this.mainStore.currentCompany &&
                (this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings)) ||
            {};

        // Fields that have Enabled flags
        const fieldsWithEnabled = [
            'trainingHeader',
            'trainingFooter',
            'reminderTrainingHeader',
            'reminderTrainingFooter',
            'assessmentHeader',
            'assessmentFooter',
            'reminderAssessmentHeader',
            'reminderAssessmentFooter',
            'welcomeMessage',
        ];

        // HTML fields that need normalization
        const htmlFields = [
            'trainingHeader',
            'trainingFooter',
            'reminderTrainingHeader',
            'reminderTrainingFooter',
            'reminderAssessmentHeader',
            'reminderAssessmentFooter',
            'assessmentHeader',
            'assessmentFooter',
            'welcomeMessage',
        ];

        // Map mrkdwn fields to their parent fields
        const mrkdwnToParent = {
            'mrkdwn_trainingHeader': 'trainingHeader',
            'mrkdwn_trainingFooter': 'trainingFooter',
            'mrkdwn_reminderTrainingHeader': 'reminderTrainingHeader',
            'mrkdwn_reminderTrainingFooter': 'reminderTrainingFooter',
            'mrkdwn_reminderAssessmentHeader': 'reminderAssessmentHeader',
            'mrkdwn_reminderAssessmentFooter': 'reminderAssessmentFooter',
            'mrkdwn_assessmentHeader': 'assessmentHeader',
            'mrkdwn_assessmentFooter': 'assessmentFooter',
            'mrkdwn_welcomeMessage': 'welcomeMessage',
        };

        // Normalize HTML field values for comparison
        // This prevents false positives when HtmlField adds trailing whitespace/newlines on focus
        const normalizeHtmlValue = (val) => {
            if (val == null) return '';
            let normalized = fixHtml(val);
            // Remove trailing whitespace and newlines for comparison
            // This prevents false positives when HtmlField adds formatting on focus
            normalized = typeof normalized === 'string' ? normalized.trimEnd() : normalized;
            return normalized;
        };

        // First pass: process all fields except mrkdwn_*
        Object.entries(values).forEach(([key, value]) => {
            if (typeof value === 'undefined') return;
            // Skip mrkdwn_* fields in first pass
            if (key.startsWith('mrkdwn_')) return;

            // Handle Enabled flags
            if (key.endsWith('Enabled')) {
                const enabledKey = `ui.email.${key}`;
                const currentEnabled = currentSettings[enabledKey];
                const nextEnabled = value === '1' ? '1' : (value === '0' ? '0' : '');

                // Only save if value changed
                // If current is undefined/null and next is '0', don't save (no need to create setting)
                // If current is undefined/null and next is '1', save '1'
                // If current exists and changed, save new value
                if (currentEnabled == null) {
                    // Setting doesn't exist yet
                    if (nextEnabled === '1') {
                        result[key] = '1';
                    }
                    // If nextEnabled is '0' or empty, don't save (no need to create)
                } else {
                    // Setting exists, save only if changed
                    if (currentEnabled !== nextEnabled) {
                        result[key] = nextEnabled;
                    }
                }
                return;
            }

            // Handle field values - only save if Enabled is '1' or field already exists
            const enabledKey = `${key}Enabled`;
            const isEnabled = values[enabledKey] === '1';
            const fieldExists = currentSettings[`ui.email.${key}`] != null;
            const hasEnabledFlag = fieldsWithEnabled.includes(key);
            const isHtmlField = htmlFields.includes(key);

            if (hasEnabledFlag) {
                // For fields with Enabled flags, only save if enabled or field already exists
                if (isEnabled || fieldExists) {
                    // For HTML fields, normalize to prevent false positives from trailing whitespace
                    const nextValue = isHtmlField 
                        ? normalizeHtmlValue(value) 
                        : (value == null ? '' : String(value));
                    const currentValue = isHtmlField
                        ? normalizeHtmlValue(currentSettings[`ui.email.${key}`])
                        : (currentSettings[`ui.email.${key}`] == null ? '' : String(currentSettings[`ui.email.${key}`]));
                    // Save if value changed or if enabling for the first time (even if empty string)
                    if (nextValue !== currentValue || (isEnabled && !fieldExists)) {
                        result[key] = value == null ? '' : value;
                    }
                }
            } else {
                // For fields without Enabled flags (subject, greeting), save if changed
                // Don't trim - allow users to control whitespace in subject/greeting
                const nextValue = value == null ? '' : String(value);
                const currentValue = currentSettings[`ui.email.${key}`] == null 
                    ? '' 
                    : String(currentSettings[`ui.email.${key}`]);
                if (nextValue !== currentValue) {
                    result[key] = value == null ? '' : value;
                }
            }
        });

        // Second pass: process mrkdwn_* fields only if their parent field is dirty
        Object.entries(values).forEach(([key, value]) => {
            if (typeof value === 'undefined') return;
            // Only process mrkdwn_* fields
            if (!key.startsWith('mrkdwn_')) return;

            const parentKey = mrkdwnToParent[key];
            if (!parentKey) return; // Unknown mrkdwn field, skip

            // Only add mrkdwn field if its parent field is in the result (is dirty)
            if (parentKey in result) {
                // Parent field is dirty, so include mrkdwn field as well
                result[key] = value == null ? '' : value;
            }
            // If parent field is not dirty, don't add mrkdwn field even if it changed
        });

        return result;
    }

    loadDefaultEmailSettings = generateLoadEntity(
        'defaultEmailSettings',
        this,
        'loadingDefaultEmailSettings',
        async (companyId) => {
            return services.Companies.getDefaultEmailSettings(companyId);
        },
        'defaultEmailSettings'
    );

    async loadSmtpSettings(companyId) {
        this.loadingSmtpSettings = true;
        this.error = null;
        this.smtpSettings = null;
        try {
            const result = await services.Companies.getSmtpSettings(companyId);
            runInAction(() => {
                this.smtpSettings = result;
            });
            return result;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            runInAction(() => {
                this.loadingSmtpSettings = false;
            });
        }
    }

    async saveSmtpSettings(companyId, values) {
        if (this.savingSmtpSettings) return false;
        this.savingSmtpSettings = true;
        this.error = null;
        try {
            const result = await services.Companies.saveSmtpSettings(
                companyId,
                values
            );
            runInAction(() => {
                this.smtpSettings = result;
                if (this.mainStore.currentCompany) {
                    const settingsObj =
                        this.mainStore.currentCompany.uiSettings ||
                        this.mainStore.currentCompany.settings;
                    if (settingsObj) {
                        settingsObj['ui.email.override_account_name'] =
                            values.entityName === 'partner' ? '0' : '1';
                    }
                }
            });
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            runInAction(() => {
                this.savingSmtpSettings = false;
            });
        }
    }

    async previewEmailSettings(companyId, type, values) {
        this.previewEmailError = null;
        try {
            return await services.Companies.previewEmail(companyId, {
                type,
                values,
            });
        } catch (e) {
            this.previewEmailError = extractErrorMessage(e);
            throw e;
        }
    }

    async saveBrandingSettings(companyId, values) {
        if (this.savingBrandingSettings.includes(companyId)) return false;
        this.savingBrandingSettings.push(companyId);
        try {
            let body = {
                'ui.minihra_banner_image_src': values.bannerImageSrc,
                'ui.minihra_banner_link_url': values.bannerLinkURL,
                'ui.minihra_finish_url': values.finishUrl,
            };
            let result = await services.Companies.update({ id: companyId, ...body });
            let company = this.companies.find((x) => x.company_id === companyId);
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings;
                settingsObj['ui.minihra_banner_image_src'] = values.bannerImageSrc;
                settingsObj['ui.minihra_banner_link_url'] = values.bannerLinkURL;
                settingsObj['ui.minihra_finish_url'] = values.finishUrl;
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingBrandingSettings.remove(companyId);
        }
    }

    async loadOnboardingSettings(companyId) {
        if (this.loadingOnboardingSettings) return;
        this.loadingOnboardingSettings = true;
        try {
            const result = await services.Companies.getOnboardingSettings(
                companyId
            );
            runInAction(() => {
                this.onboardingSettings = result;
            });
            return result;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            runInAction(() => {
                this.loadingOnboardingSettings = false;
            });
        }
    }

    async saveOnboardingSettings(companyId, data) {
        if (this.savingOnboardingSettings) return;
        this.savingOnboardingSettings = true;
        try {
            const result = await services.Companies.saveOnboardingSettings(
                companyId,
                data
            );
            runInAction(() => {
                this.onboardingSettings = result;
            });
            return result;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            runInAction(() => {
                this.savingOnboardingSettings = false;
            });
        }
    }

    async saveEnrolmentSetting(companyId, value) {
        if (this.savingEnrolmentSetting.includes(companyId)) return false;
        this.savingEnrolmentSetting.push(companyId);
        try {
            let body = { 'ui.sendEnrolment': value };
            let result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings;
                settingsObj['ui.sendEnrolment'] = value;
            }
        } catch (e) {
            //console.error('Save failed: ', e);
            this.error = extractErrorMessage(e);
        } finally {
            this.savingEnrolmentSetting.remove(companyId);
        }
    }

    async saveInviteOnListAddSetting(companyId, value) {
        if (this.savingInviteOnListAddSetting.includes(companyId)) return false;
        this.savingInviteOnListAddSetting.push(companyId);
        try {
            const body = { 'ui.assessment.inviteOnListAdd': value };
            const result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            const company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings;
                settingsObj['ui.assessment.inviteOnListAdd'] = value;
            }
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.savingInviteOnListAddSetting.remove(companyId);
        }
    }

    async saveFastForwardSetting(companyId, value) {
        if (this.savingFastForwardSetting.includes(companyId)) return false;
        this.savingFastForwardSetting.push(companyId);
        try {
            const body = { 'ui.disable_fast_forward': value };
            const result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            const company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings;
                settingsObj['ui.disable_fast_forward'] = value;
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            this.savingFastForwardSetting.remove(companyId);
        }
    }

    async saveHideObsoleteBouncesSetting(companyId, value) {
        if (this.savingHideObsoleteBouncesSetting.includes(companyId))
            return false;
        this.savingHideObsoleteBouncesSetting.push(companyId);
        try {
            const body = { 'ui.bounced.hideObsolete': value };
            const result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            const company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings;
                settingsObj['ui.bounced.hideObsolete'] = value;
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            this.savingHideObsoleteBouncesSetting.remove(companyId);
        }
    }

    /**
     * Save assessment recommendation content settings.
     * @param {number} companyId
     * @param {Object} values partial map of setting keys → string/JSON values
     */
    async saveRecommendationSettings(companyId, values) {
        if (this.savingRecommendationSettings.includes(companyId)) return false;
        this.savingRecommendationSettings.push(companyId);
        try {
            const body = {};
            Object.entries(values || {}).forEach(([key, value]) => {
                if (
                    key === 'ui.assessment.recommendations.create_as_one' ||
                    key ===
                        'ui.assessment.recommendations.include_not_completed' ||
                    key === 'ui.assessment.recommendations.subjects_by_area' ||
                    key ===
                        'ui.assessment.recommendations.training.name_template' ||
                    key ===
                        'ui.assessment.recommendations.training.list_name_template' ||
                    key ===
                        'ui.assessment.recommendations.training.due_days' ||
                    key ===
                        'ui.assessment.recommendations.training.sending_time' ||
                    key ===
                        'ui.assessment.recommendations.training.schedule_type' ||
                    key ===
                        'ui.assessment.recommendations.training.reminders_enabled' ||
                    key ===
                        'ui.assessment.recommendations.training.reminders_settings'
                ) {
                    body[key] =
                        typeof value === 'string'
                            ? value
                            : JSON.stringify(value);
                }
            });
            if (Object.keys(body).length === 0) return false;

            const result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            const company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings ||
                    {};
                Object.assign(settingsObj, body);
                if (!this.mainStore.currentCompany.uiSettings) {
                    this.mainStore.currentCompany.uiSettings = settingsObj;
                }
                if (!this.mainStore.currentCompany.settings) {
                    this.mainStore.currentCompany.settings = settingsObj;
                }
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            this.savingRecommendationSettings.remove(companyId);
        }
    }

    async saveOrganizeEmployeeListsByOrganizationSetting(companyId, value) {
        if (this.savingOrganizeEmployeeListsByOrganization.includes(companyId))
            return false;
        this.savingOrganizeEmployeeListsByOrganization.push(companyId);
        try {
            const body = {
                'ui.organize_employee_lists_by_organization': value,
            };
            const result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            const company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings;
                settingsObj['ui.organize_employee_lists_by_organization'] =
                    value;
            }
            return true;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            this.savingOrganizeEmployeeListsByOrganization.remove(companyId);
        }
    }

    async loadAwareExposeConfig(companyId) {
        if (this.loadingAwareExposeConfig) return this.awareExposeConfig;
        this.loadingAwareExposeConfig = true;
        try {
            const result = await services.Companies.getAwareExposeConfig(
                companyId
            );
            runInAction(() => {
                this.awareExposeConfig = result;
            });
            return result;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            runInAction(() => {
                this.loadingAwareExposeConfig = false;
            });
        }
    }

    async saveAwareExposeSettings(companyId, values) {
        if (this.savingAwareExposeSettings.includes(companyId)) return false;
        this.savingAwareExposeSettings.push(companyId);
        try {
            const body = {};
            if (typeof values.enabled === 'boolean') {
                body['aware_expose.enabled'] = values.enabled ? '1' : '0';
            }
            if (Array.isArray(values.allowedDomains)) {
                body['aware_expose.allowed_email_domains'] =
                    values.allowedDomains;
            }
            if (Array.isArray(values.chainScan)) {
                body['aware_expose.llm_chain_scan'] = values.chainScan;
            }
            if (Array.isArray(values.chainAttacks)) {
                body['aware_expose.llm_chain_attacks'] = values.chainAttacks;
            }
            if (Object.keys(body).length === 0) return true;
            const result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            const company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings ||
                    {};
                Object.assign(settingsObj, body);
            }
            const fresh = await services.Companies.getAwareExposeConfig(
                companyId
            );
            runInAction(() => {
                this.awareExposeConfig = fresh;
            });
            return fresh;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            this.savingAwareExposeSettings.remove(companyId);
        }
    }

    async rotateAwareExposeKey(companyId) {
        if (this.rotatingAwareExposeKey.includes(companyId)) return null;
        this.rotatingAwareExposeKey.push(companyId);
        try {
            const result = await services.Companies.rotateAwareExposeKey(
                companyId
            );
            if (this.awareExposeConfig) {
                runInAction(() => {
                    this.awareExposeConfig = {
                        ...this.awareExposeConfig,
                        publicUrlKey: result.publicUrlKey,
                    };
                });
            }
            return result.publicUrlKey;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            this.rotatingAwareExposeKey.remove(companyId);
        }
    }

    async saveSecuritySettings(companyId, values) {
        const body = {};
        // MY (end-user portal) settings
        if (values.myLoginLinkValidity != null) {
            body['security.my_login_link_validity'] = values.myLoginLinkValidity;
        }
        if (values.mySessionDuration != null) {
            body['security.my_session_duration'] = values.mySessionDuration;
        }
        // LMS (admin portal) settings
        if (values.lmsLoginLinkValidity != null) {
            body['security.lms_login_link_validity'] = values.lmsLoginLinkValidity;
        }
        if (values.lmsSessionDuration != null) {
            body['security.lms_session_duration'] = values.lmsSessionDuration;
        }
        if (Object.keys(body).length === 0) return;
        try {
            const result = await services.Companies.update({
                id: companyId,
                ...body,
            });
            const company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result);
            }
            if (this.mainStore.currentCompany) {
                const settingsObj =
                    this.mainStore.currentCompany.uiSettings ||
                    this.mainStore.currentCompany.settings || {};
                Object.assign(settingsObj, body);
            }
        } catch (e) {
            this.error = extractErrorMessage(e);
        }
    }

    async uploadLogo(companyId, formData) {
        this.uploadingLogo = true;
        try {
            let result = await services.Resources.addLogo(companyId, formData);
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result, { logo_name: result.logo_name });
            }
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.uploadingLogo = false;
        }
    }

    async uploadLogo2(company, formData) {
        this.uploadingLogo = true;
        try {
            let result = await services.Resources.addLogo(company.id, formData);
            //let company = this.companies.find(x => x.company_id === companyId);
            if (company) {
                Object.assign(company, result, { logo_name: result.logo_name });
            }
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.uploadingLogo = false;
        }
    }

    async removeLogo(companyId) {
        try {
            let result = await services.Resources.removeLogo(companyId);
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                Object.assign(company, result, { logo_name: null });
            }
            //await services.Resources.removeLogo(companyId);
        } catch (e) {
            this.error = extractErrorMessage(e);
        }
    }

    async addManager(companyId, data) {
        this.addingManager = true;
        this.addManagerError = null;
        try {
            let result = await services.Companies.managersService(
                companyId
            ).create(data);
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company) {
                if (!company.managers) company.managers = [];

                company.managers.push(result);
            }
            await this.loadCompanies(true);
            return true;
        } catch (e) {
            this.addManagerError = extractErrorMessage(e);
            throw e;
        } finally {
            this.addingManager = false;
        }
    }

    async removeManager(companyId, email) {
        this.error = null;
        try {
            await services.Companies.managersService(companyId).delete(email);
            let company = this.companies.find(
                (x) => x.company_id === companyId
            );
            if (company && company.managers) {
                runInAction(() => {
                    company.managers = company.managers.filter(
                        (manager) => manager.email !== email
                    );
                });
            }
            return this.loadCompanies(true);
        } catch (e) {
            this.error = extractErrorMessage(e);
        }
    }

    setCompany = (company) => {
        this.company = company;
    };

    async loadIntegrationData(companyId, integration) {
        let loadingKey = `loading${upper(integration)}Datas`;
        let dataKey = `${integration}Datas`;
        if (this[loadingKey].includes(companyId)) return false;
        this[loadingKey].push(companyId);
        try {
            let data = await services.Companies.integrationsService(
                companyId
            ).fetch(integration); //videoId
            let t = this[dataKey].find((x) => x.id === companyId);
            if (t) this[dataKey].remove(t);
            this[dataKey].push({ id: companyId, ...data });
        } finally {
            this[loadingKey].remove(companyId);
        }
    }

    async loadIntegrationGroups(companyId, integration) {
        let key = `loading${upper(integration)}Groups`;
        let errorKey = `${integration}Error`;

        if (this[key].includes(companyId)) return false;
        this[errorKey] = '';

        this[key].push(companyId);
        try {
            let data = await services.Companies.integrationsService(
                companyId
            ).integrationGroups(integration); //videoId
            this[`${integration}Groups`].replace(data);
            return data;
        } catch (e) {
            this[errorKey] = extractErrorMessage(e);
        } finally {
            this[key].remove(companyId);
        }
    }

    async saveIntegrationData(companyId, integration, data) {
        const key = `updateIntegrationData__${integration}`;

        if (!this[key]) {
            const savingKey = `saving${integration[0].toUpperCase()}${integration.slice(1)}Data`;

            this[savingKey] = [];
            this[key] = generateUpdateEntity(
                `integration`,
                this,
                savingKey,
                async (companyId, integration, data) => {
                    const resp = await services.Companies
                        .integrationsService(companyId)
                        .update(data, integration);

                    const stateKey = `${integration}Datas`;
                    const rec = this[stateKey].find(x => x.id === companyId);
                    if (rec) Object.assign(rec, resp);
                    else this[stateKey].push({ id: companyId, ...resp });
                },
                null,
                { throwError: true }
            );
        }

        return await this[key](companyId, integration, data);
    }

    async saveIntegrationRule(
        companyId,
        integration,
        entityType,
        externalId,
        rule
    ) {
        try {
            await services.Companies.integrationsService(
                companyId
            ).saveIntegrationRule(integration, {
                entityType,
                externalId,
                rule,
            });

            if (entityType === 1) {
                let g = this[`${integration}Groups`].find(
                    (x) => x.id === externalId
                );
                if (g) g.rule = rule;
            }
        } catch (e) {
            // Intentionally suppressed
        }
    }

    async saveIntegrationRules(companyId, integration, entityType, values) {
        try {
            await services.Companies.integrationsService(
                companyId
            ).saveIntegrationRules(integration, { entityType, values });
            return true;
        } catch (e) {
            // Intentionally suppressed
        }
    }

    async testIntegration(companyId, integration, data) {
        let key = `testing${upper(integration)}Data`;

        if (this[key].includes(companyId)) return false;

        this[key].push(companyId);
        try {
            let response = await services.Companies.integrationsService(
                companyId
            ).testIntegrationData(integration, data);
            return response;
        } finally {
            this[key].remove(companyId);
        }
    }

    unlinkIntegration = generateDeleteEntity(
        'deleteIntegration',
        this,
        'deletingIntegration',
        async (companyId, integration) => {
            return services.Companies.integrationsService(companyId).delete(
                integration
            );
        }
    );

    async generateScimKey(companyId) {
        if (this.generatingScimKey.includes(companyId)) return;
        
        this.generatingScimKey.push(companyId);
        try {
            const response = await services.Companies.integrationsService(companyId)
                .generateScimKey('azuresso');
            this.scimKeyGenerated = response.secret;
            
            // Update the integration data with the masked value from server
            const azuressoData = this.azuressoDataIndex[companyId];
            if (azuressoData && response.maskedSecret) {
                azuressoData.scimSecret = response.maskedSecret;
            } else if (azuressoData && response.secret) {
                // Create masked version: first 2 letters + *** + last 2 letters
                const secret = response.secret;
                const maskedSecret = secret.length >= 4 
                    ? secret.substring(0, 2) + '***' + secret.substring(secret.length - 2)
                    : '***'; // fallback for very short secrets
                azuressoData.scimSecret = maskedSecret;
            }
            
            return response.secret;
        } catch (e) {
            this.error = extractErrorMessage(e);
            throw e;
        } finally {
            this.generatingScimKey.remove(companyId);
        }
    }

    clearScimKey() {
        this.scimKeyGenerated = null;
    }

    async syncIntegration(companyId, integration, data) {
        if (this.syncRunning) return;
        this.syncRunning = true;
        try {
            let response = await services.Companies.integrationsService(
                companyId
            ).syncIntegration(integration, data);
            if (response.lastSync || response.latestRunningSyncId) {
                let existing = this[`${integration}DataIndex`][companyId];
                if (existing) {
                    Object.assign(existing, response);
                }
            }

            return response;
        } finally {
            this.syncRunning = false;
        }
    }

    updateSyncdata(companyId, data) {
        //console.log('updateSyncdata', companyId, data);
        if (this.azureDataIndex[companyId])
            Object.assign(this.azureDataIndex[companyId], data);
        if (this.googleDataIndex[companyId])
            Object.assign(this.googleDataIndex[companyId], data);
    }

    removeUserFromExcludedList(companyId, product, id) {
        const data = this[`${product}DataIndex`][companyId];
        if (data && data.excludedUsers) {
            data.excludedUsers.replace(
                data.excludedUsers.filter((x) => x.id !== id)
            );
        }
    }

    get azureDataIndex() {
        return keyBy(this.azureDatas, 'id');
    }

    get vantaDataIndex() {
        return keyBy(this.vantaDatas, 'id');
    }

    get vantasyncDataIndex() {
        return keyBy(this.vantasyncDatas, 'id');
    }

    get teamsDataIndex() {
        return keyBy(this.teamsDatas, 'id');
    }

    get azuressoDataIndex() {
        return keyBy(this.azuressoDatas, 'id');
    }

    get googleDataIndex() {
        return keyBy(this.googleDatas, 'id');
    }

    get slackDataIndex() {
        return keyBy(this.slackDatas, 'id');
    }

    get connectedIntegrations() {
        if (this.integrations.length === 0) return [];
        return this.integrations.filter((integration) => {
            return integration.connected;
        });
    }

    get availableIntegrations() {
        if (this.integrations.length === 0) return [];
        let result = this.integrations.filter(
            (integration) => !integration.connected
        );
        this.mainStore.havePartnerSubscription &&
            result.forEach(
                (integration) =>
                    (integration.needsUpgrade = !(
                        this.mainStore.currentCompany.subscription
                            .allowedItems ||
                        this.mainStore.currentCompany.subscription
                            .subscription_definition
                    ).integrations.includes(integration.id))
            );
        return result;
    }
}

export default CompanyStore;
